import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { TypedDocumentNode } from '@apollo/client'
import { Box, Text, Wizard } from '@factorialco/deprecated-design-system'
import {
  CreateProjectTaskDocument,
  CreateProjectTaskMutationVariables,
  ProjectWorkerForSelectFragment,
  SubprojectForSelectFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ProjectTaskForm } from 'modules/project_management/components/TaskForm/ProjectTaskForm'
import { useTaskForm } from 'modules/project_management/components/wizards/AddTaskWizard/utils'
import { isValidTaskStatus } from 'modules/tasks/lib/status'

interface Props {
  hasProjectWorkers: boolean
  onClose: () => void
  refetchQueries: TypedDocumentNode[]
  taskStatus: string
}

export const AddTaskWizard = ({
  hasProjectWorkers,
  onClose,
  refetchQueries,
  taskStatus,
}: Props) => {
  const { id: projectId } = useParams()
  const { values } = useTaskForm(Number(projectId), null)
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<CreateProjectTaskMutationVariables>(values)
  const [files, setFiles] = useState<File[] | null | undefined>(values.files)
  const fromKanban = searchParams.get('view') === 'kanban'

  const [selectedSubproject, setSelectedSubproject] = useState<SubprojectForSelectFragment | null>(
    null
  )
  const [selectedProjectWorkers, setSelectedProjectWorkers] = useState<
    ProjectWorkerForSelectFragment[]
  >([])

  const [createProjectTask] = useMutation(CreateProjectTaskDocument, {
    extractorFn: (mutationData) =>
      mutationData.projectManagementMutations.createProjectmanagementProjectTask,
  })

  useEffect(() => {
    if (taskStatus) {
      if (isValidTaskStatus(taskStatus)) {
        setData({ ...data, status: taskStatus })
      }
    }
  }, [taskStatus])

  const validateForm = () => {
    if (data.name.length > 0) {
      return true
    }
    return false
  }

  const buildProjectWorkers = () => {
    return selectedProjectWorkers.flatMap((worker) => {
      const fullName = worker.employee?.access?.fullName

      return fullName
        ? {
            label: fullName,
            url: buildUrl(worker.employee?.access?.avatar),
          }
        : []
    })
  }

  const trackEvent = () => {
    track(trackingEvents.projectManagement.tasks.submitCreateProjectTask, {
      subproject_linked: !!data.subprojectId,
      assignees_count: data.assigneeEmployeeIds?.length,
      attached_documents_count: files?.length,
      updates_notifications: !!data.followUp,
      origin: fromKanban ? 'kanban' : 'datatable',
    })
  }

  const onSubmit = async () => {
    trackEvent()
    const result = await createProjectTask({
      variables: {
        ...data,
        files,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.tasks.create_task.success_message'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('project_management.tasks.create_task.error_message'),
        time: 2000,
      })
    }
    onClose()
  }

  const steps = [
    {
      title: i18n.t('project_management.tasks.form.steps.setup'),
      nextText: i18n.t('project_management.tasks.form.steps.submit'),
      render: () => (
        <Box paddingRight="s120">
          <Box paddingBottom="s16" gap="s8">
            <Text.Header2>{i18n.t('project_management.tasks.form.steps.setup')}</Text.Header2>
            <Text.Normal>
              {i18n.t('project_management.tasks.form.steps.setup_description')}
            </Text.Normal>
          </Box>
          <ProjectTaskForm<CreateProjectTaskMutationVariables>
            task={data}
            initialFiles={[]}
            onChange={(value) => setData(value)}
            onFilesChange={(taskFiles) => setFiles(taskFiles)}
            onSubprojectChange={setSelectedSubproject}
            onProjectWorkersChange={setSelectedProjectWorkers}
            hasProjectWorkers={hasProjectWorkers}
          />
        </Box>
      ),
      isCompleted: () => validateForm(),
    },
  ]

  return (
    <Wizard.Modal
      icon="multiple-buildings"
      title={i18n.t('project_management.tasks.create_task.title')}
      description={i18n.t('project_management.tasks.create_task.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      steps={steps}
      initialData={{}}
      size="large"
    ></Wizard.Modal>
  )
}
